import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getAccessToken, getRefreshToken, saveTokens, clearTokens } from '../utils/authTokens';

export interface User {
  userName: string;
  userEmail: string;
  country: string;
  roles: string[];
  permissions: string[];
  userId: number;
}

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  isInitialized: boolean;
}

interface UserContextType {
  // Auth state
  authState: AuthState;
  
  // Legacy compatibility
  user: User | null;
  isLoading: boolean;
  
  // Actions
  setUser: (user: User | null) => void;
  clearUser: () => void;
  login: (tokens: { accessToken: string; refreshToken: string }, user?: User) => void;
  logout: () => void;
  updateTokens: (tokens: { accessToken: string; refreshToken: string }) => void;
  setLoading: (loading: boolean) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const USER_STORAGE_KEY = 'user_data';

// Get initial auth state
const getInitialAuthState = (): AuthState => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  const userInfo = localStorage.getItem(USER_STORAGE_KEY);

  return {
    accessToken,
    refreshToken,
    isAuthenticated: !!(accessToken && refreshToken),
    isLoading: true,
    user: userInfo ? JSON.parse(userInfo) : null,
    isInitialized: false
  };
};

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(getInitialAuthState());

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = () => {
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();
      const storedUser = localStorage.getItem(USER_STORAGE_KEY);
      
      let user: User | null = null;
      
      if (storedUser) {
        try {
          user = JSON.parse(storedUser);
        } catch (error) {
          console.error('Failed to parse stored user data:', error);
          localStorage.removeItem(USER_STORAGE_KEY);
        }
      }

      setAuthState({
        accessToken,
        refreshToken,
        isAuthenticated: !!(accessToken && refreshToken && user),
        isLoading: false,
        user,
        isInitialized: true
      });
    };

    // Small delay to ensure proper initialization
    const timer = setTimeout(initializeAuth, 100);
    return () => clearTimeout(timer);
  }, []);

  // Login action
  const login = (tokens: { accessToken: string; refreshToken: string }) => {
    // Save tokens
    saveTokens(tokens.accessToken, tokens.refreshToken);
    
    // Save user if provided
   
    // Update state
    setAuthState({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      isAuthenticated: true,
      isLoading: false,
      user: null,
      isInitialized: true
    });

    console.log('✅ Login successful');
  };

  // Logout action
  const logout = () => {
    // Clear tokens
    clearTokens();
    
    // Clear user data
    localStorage.removeItem(USER_STORAGE_KEY);

    // Update state
    setAuthState({
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      user: null,
      isInitialized: true
    });

    console.log('✅ Logout successful');
  };

  // Set user action
  const setUser = (userData: User | null) => {
    if (userData) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
    } else {
      localStorage.removeItem(USER_STORAGE_KEY);
    }

    setAuthState(prev => ({
      ...prev,
      user: userData,
      isAuthenticated: !!(prev.accessToken && prev.refreshToken && userData)
    }));
  };

  // Clear user action
  const clearUser = () => {
    localStorage.removeItem(USER_STORAGE_KEY);
    
    setAuthState(prev => ({
      ...prev,
      user: null,
      isAuthenticated: false
    }));
  };

  // Update tokens action
  const updateTokens = (tokens: { accessToken: string; refreshToken: string }) => {
    saveTokens(tokens.accessToken, tokens.refreshToken);
    
    setAuthState(prev => ({
      ...prev,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      isAuthenticated: !!(tokens.accessToken && tokens.refreshToken && prev.user)
    }));

    console.log('✅ Tokens updated');
  };

  // Set loading action
  const setLoading = (loading: boolean) => {
    setAuthState(prev => ({
      ...prev,
      isLoading: loading
    }));
  };

  return (
    <UserContext.Provider 
      value={{ 
        authState,
        // Legacy compatibility
        user: authState.user,
        isLoading: authState.isLoading,
        // Actions
        setUser,
        clearUser,
        login,
        logout,
        updateTokens,
        setLoading
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Additional hooks for convenience
export const useAuth = () => {
  const { authState, login, logout, updateTokens } = useUser();
  return { authState, login, logout, updateTokens };
};

export const useIsAuthenticated = () => {
  const { authState } = useUser();
  return authState.isAuthenticated;
};